// Report Data Helpers and Utilities

/**
 * Calculate KPIs for warnings
 */
export const calculateWarningKPIs = (warnings) => {
  const total = warnings.length;
  const highRisk = warnings.filter(w => w.riskLevel === 'high').length;
  const mediumRisk = warnings.filter(w => w.riskLevel === 'medium').length;
  const lowRisk = warnings.filter(w => w.riskLevel === 'low').length;
  const newWarnings = warnings.filter(w => w.status === 'new').length;
  const inProgress = warnings.filter(w => w.status === 'in_progress').length;
  const resolved = warnings.filter(w => w.status === 'resolved').length;

  // Calculate average score
  const avgScore = total > 0 
    ? (warnings.reduce((sum, w) => sum + w.score, 0) / total).toFixed(1)
    : 0;

  // Calculate average resolution time (in days)
  const resolvedWithTime = warnings.filter(w => w.status === 'resolved' && w.resolvedDate && w.createdDate);
  const avgResolutionTime = resolvedWithTime.length > 0
    ? (resolvedWithTime.reduce((sum, w) => {
        const created = new Date(w.createdDate);
        const resolved = new Date(w.resolvedDate);
        const diffDays = (resolved - created) / (1000 * 60 * 60 * 24);
        return sum + diffDays;
      }, 0) / resolvedWithTime.length).toFixed(1)
    : 0;

  // New warnings in last 7 days
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  const newIn7Days = warnings.filter(w => {
    const created = new Date(w.createdDate);
    return created >= sevenDaysAgo;
  }).length;

  return {
    total,
    highRisk,
    mediumRisk,
    lowRisk,
    newWarnings,
    inProgress,
    resolved,
    avgScore,
    avgResolutionTime,
    newIn7Days,
    highRiskPercent: total > 0 ? ((highRisk / total) * 100).toFixed(1) : 0,
    mediumRiskPercent: total > 0 ? ((mediumRisk / total) * 100).toFixed(1) : 0,
    lowRiskPercent: total > 0 ? ((lowRisk / total) * 100).toFixed(1) : 0
  };
};

/**
 * Get risk distribution data for pie/doughnut chart
 */
export const getRiskDistributionData = (warnings) => {
  const kpis = calculateWarningKPIs(warnings);
  
  return {
    labels: ['Rủi ro cao', 'Rủi ro trung bình', 'Rủi ro thấp'],
    datasets: [{
      data: [kpis.highRisk, kpis.mediumRisk, kpis.lowRisk],
      backgroundColor: ['#dc3545', '#fd7e14', '#ffc107'],
      borderWidth: 2,
      borderColor: '#fff'
    }]
  };
};

/**
 * Get trend data for line chart (last 30 days)
 */
export const getTrendData = (warnings, days = 30) => {
  const today = new Date();
  const labels = [];
  const dataPoints = [];

  for (let i = days - 1; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = `${date.getDate()}/${date.getMonth() + 1}`;
    labels.push(dateStr);

    // Count warnings created on or before this date
    const count = warnings.filter(w => {
      const created = new Date(w.createdDate);
      return created <= date;
    }).length;
    dataPoints.push(count);
  }

  return {
    labels,
    datasets: [{
      label: 'Tổng số cảnh báo',
      data: dataPoints,
      borderColor: '#0d6efd',
      backgroundColor: 'rgba(13, 110, 253, 0.1)',
      tension: 0.3,
      fill: true
    }]
  };
};

/**
 * Get warnings by customer type
 */
export const getWarningsByCustomerType = (warnings) => {
  const individual = warnings.filter(w => w.customerType === 'Khách hàng cá nhân').length;
  const corporate = warnings.filter(w => w.customerType === 'Khách hàng doanh nghiệp').length;

  return {
    labels: ['Cá nhân', 'Doanh nghiệp'],
    datasets: [{
      label: 'Số lượng cảnh báo',
      data: [individual, corporate],
      backgroundColor: ['#0dcaf0', '#6f42c1']
    }]
  };
};

/**
 * Get warnings by branch
 */
export const getWarningsByBranch = (warnings, customers) => {
  const branchMap = {};
  
  warnings.forEach(w => {
    const customer = customers.find(c => c.id === w.customerId);
    if (customer && customer.branch) {
      branchMap[customer.branch] = (branchMap[customer.branch] || 0) + 1;
    }
  });

  return {
    labels: Object.keys(branchMap),
    datasets: [{
      label: 'Số cảnh báo',
      data: Object.values(branchMap),
      backgroundColor: '#20c997'
    }]
  };
};

/**
 * Get model performance data
 */
export const getModelPerformance = (warnings, models) => {
  const modelMap = {};
  
  warnings.forEach(w => {
    const modelName = w.modelName || 'Unknown';
    if (!modelMap[modelName]) {
      modelMap[modelName] = { high: 0, medium: 0, low: 0 };
    }
    modelMap[modelName][w.riskLevel]++;
  });

  return {
    labels: Object.keys(modelMap),
    datasets: [
      {
        label: 'Cao',
        data: Object.values(modelMap).map(m => m.high),
        backgroundColor: '#dc3545'
      },
      {
        label: 'Trung bình',
        data: Object.values(modelMap).map(m => m.medium),
        backgroundColor: '#fd7e14'
      },
      {
        label: 'Thấp',
        data: Object.values(modelMap).map(m => m.low),
        backgroundColor: '#ffc107'
      }
    ]
  };
};

/**
 * Filter warnings by date range
 */
export const filterByDateRange = (warnings, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return warnings;

  const from = dateFrom ? new Date(dateFrom) : null;
  const to = dateTo ? new Date(dateTo) : null;

  return warnings.filter(w => {
    const created = new Date(w.createdDate);
    if (from && created < from) return false;
    if (to && created > to) return false;
    return true;
  });
};

/**
 * Filter warnings by status
 */
export const filterByStatus = (warnings, status) => {
  if (!status || status === 'all') return warnings;
  return warnings.filter(w => w.status === status);
};

/**
 * Filter warnings by risk level
 */
export const filterByRiskLevel = (warnings, riskLevel) => {
  if (!riskLevel || riskLevel === 'all') return warnings;
  return warnings.filter(w => w.riskLevel === riskLevel);
};

/**
 * Sort warnings
 */
export const sortWarnings = (warnings, sortBy = 'createdDate', order = 'desc') => {
  const sorted = [...warnings].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    // Handle date strings
    if (sortBy.includes('Date')) {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }

    if (order === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  return sorted;
};

/**
 * Get top indicators causing high risk
 */
export const getTopRiskIndicators = (warnings) => {
  const indicatorMap = {};

  warnings.filter(w => w.riskLevel === 'high').forEach(w => {
    w.indicators?.forEach(ind => {
      if (ind.riskLevel === 'high') {
        const key = ind.name;
        indicatorMap[key] = (indicatorMap[key] || 0) + 1;
      }
    });
  });

  // Sort and get top 5
  const sorted = Object.entries(indicatorMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return {
    labels: sorted.map(([name]) => name),
    datasets: [{
      label: 'Số lần xuất hiện',
      data: sorted.map(([, count]) => count),
      backgroundColor: '#dc3545'
    }]
  };
};

/**
 * Calculate aging for warnings
 */
export const calculateAging = (warning) => {
  const created = new Date(warning.createdDate);
  const now = new Date();
  const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
  
  let agingCategory = 'Mới';
  if (diffDays > 30) agingCategory = 'Quá hạn';
  else if (diffDays > 14) agingCategory = 'Cảnh báo';
  else if (diffDays > 7) agingCategory = 'Bình thường';
  
  return {
    days: diffDays,
    category: agingCategory
  };
};

/**
 * Get indicator category distribution
 */
export const getIndicatorCategoryDistribution = (indicators) => {
  const categoryMap = {};
  
  indicators.forEach(ind => {
    categoryMap[ind.category] = (categoryMap[ind.category] || 0) + 1;
  });

  return {
    labels: Object.keys(categoryMap),
    datasets: [{
      data: Object.values(categoryMap),
      backgroundColor: ['#0dcaf0', '#fd7e14', '#20c997', '#6f42c1']
    }]
  };
};

/**
 * Export data to CSV format
 */
export const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

/**
 * Format number with thousand separators
 */
export const formatNumber = (num) => {
  if (!num) return '0';
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Get risk level badge class
 */
export const getRiskBadgeClass = (riskLevel) => {
  const badges = {
    high: 'danger',
    medium: 'warning',
    low: 'success',
    none: 'secondary'
  };
  return badges[riskLevel] || 'secondary';
};

/**
 * Get status badge class
 */
export const getStatusBadgeClass = (status) => {
  const badges = {
    new: 'primary',
    in_progress: 'info',
    resolved: 'success',
    closed: 'secondary'
  };
  return badges[status] || 'secondary';
};

/**
 * Get status text in Vietnamese
 */
export const getStatusText = (status) => {
  const texts = {
    new: 'Mới',
    in_progress: 'Đang xử lý',
    resolved: 'Đã xử lý',
    closed: 'Đã đóng'
  };
  return texts[status] || status;
};

/**
 * Get unique branches from customers
 */
export const getUniqueBranches = (customers) => {
  const branches = [...new Set(customers.map(c => c.branch).filter(Boolean))];
  return branches.sort();
};

/**
 * Filter warnings by branch (requires customers data for lookup)
 * Supports both single branch (string) and multiple branches (array)
 */
export const filterByBranch = (warnings, customers, selectedBranch) => {
  // Handle 'all' or empty selection
  if (!selectedBranch || 
      selectedBranch === 'all' || 
      (Array.isArray(selectedBranch) && (selectedBranch.length === 0 || selectedBranch.includes('all')))) {
    return warnings;
  }
  
  // Create customer to branch mapping
  const customerBranchMap = {};
  customers.forEach(c => {
    customerBranchMap[c.code] = c.branch;
    customerBranchMap[c.name] = c.branch;
  });
  
  // Filter warnings by branch(es)
  return warnings.filter(w => {
    const branch = customerBranchMap[w.customerId] || customerBranchMap[w.customerName];
    
    // Multi-select: check if branch is in the array
    if (Array.isArray(selectedBranch)) {
      return selectedBranch.includes(branch);
    }
    
    // Single-select: exact match
    return branch === selectedBranch;
  });
};

